import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Building2, Bookmark, CalendarClock, Sparkles, ThumbsUp, ThumbsDown, Clock, ShieldCheck, ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";

function StatCard({ icon: Icon, label, value, tone = "bg-slate-900" }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl text-white ${tone}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
          <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
        </div>
      </div>
    </div>
  );
}

const FB_META = {
  scaduto: { label: "Scaduto", Icon: Clock, cls: "bg-amber-50 text-amber-700" },
  falso_positivo: { label: "Falso positivo", Icon: ThumbsDown, cls: "bg-red-50 text-red-700" },
};

export default function Riepilogo() {
  const [summary, setSummary] = useState(null);
  const [fb, setFb] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/summary"), api.get("/feedback/summary")])
      .then(([s, f]) => { setSummary(s.data); setFb(f.data); })
      .catch(() => {});
  }, []);

  if (!summary || !fb) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const t = summary.totali;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <p className="eyebrow text-indigo-600">Spazio studio</p>
      <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">Riepilogo</h1>
      <p className="mt-1 text-slate-500">Panoramica di tutti i tuoi clienti e affidabilità del motore in base alle tue segnalazioni.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Building2} label="Clienti" value={t.clienti} tone="bg-indigo-600" />
        <StatCard icon={Bookmark} label="Bandi salvati" value={t.bandi_salvati} tone="bg-slate-900" />
        <StatCard icon={CalendarClock} label="Scadenze 30gg" value={t.scadenze_30gg} tone="bg-amber-500" />
        <StatCard icon={Sparkles} label="Nuovi bandi" value={t.nuovi_bandi} tone="bg-emerald-600" />
      </div>

      {/* Per-cliente */}
      <div className="mt-8">
        <h2 className="font-display text-lg font-bold text-slate-900">Clienti</h2>
        {summary.clienti.length === 0 ? (
          <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="riepilogo-empty">
            <p className="text-sm text-slate-500">Nessun cliente ancora. Aggiungi un'azienda per iniziare.</p>
            <Button asChild className="mt-4"><Link to="/app/aziende/nuova">Aggiungi cliente</Link></Button>
          </div>
        ) : (
          <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-3 py-3">Salvati</th>
                    <th className="px-3 py-3">Scadenze</th>
                    <th className="px-3 py-3">Nuovi</th>
                    <th className="px-3 py-3">Auto</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody data-testid="riepilogo-clients">
                  {summary.clienti.map((c) => (
                    <tr key={c.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50" data-testid="riepilogo-client-row">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{c.ragione_sociale}</div>
                        <div className="text-xs text-slate-400">{c.ateco} · {c.regione}</div>
                      </td>
                      <td className="px-3 py-3 font-semibold text-slate-700">{c.salvati}</td>
                      <td className="px-3 py-3">
                        {c.scadenze > 0
                          ? <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700">{c.scadenze}</span>
                          : <span className="text-slate-300">0</span>}
                      </td>
                      <td className="px-3 py-3">
                        {c.nuovi > 0
                          ? <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">{c.nuovi}</span>
                          : <span className="text-slate-300">0</span>}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.auto_search ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {c.auto_search ? "ON" : "OFF"}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <Button asChild size="sm" variant="outline" className="gap-1.5" data-testid="riepilogo-open-client">
                          <Link to={`/app/aziende/${c.id}/bandi`}><Search className="h-3.5 w-3.5" /> Bandi</Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Cruscotto segnalazioni */}
      <div className="mt-10">
        <h2 className="font-display text-lg font-bold text-slate-900">Cruscotto segnalazioni</h2>
        <p className="mt-1 text-sm text-slate-500">Basato sui tuoi tocchi "pertinente / scaduto / falso positivo": mostra dove il motore sbaglia più spesso.</p>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              <span className="text-xs uppercase tracking-wide text-slate-400">Affidabilità</span>
            </div>
            <div className="mt-2 font-display text-3xl font-bold text-slate-900" data-testid="feedback-reliability">
              {fb.affidabilita !== null ? `${fb.affidabilita}%` : "—"}
            </div>
            <div className="text-xs text-slate-400">su {fb.totale} segnalazioni</div>
          </div>
          <StatCard icon={ThumbsUp} label="Pertinenti" value={fb.counts.pertinente} tone="bg-emerald-600" />
          <StatCard icon={Clock} label="Scaduti" value={fb.counts.scaduto} tone="bg-amber-500" />
          <StatCard icon={ThumbsDown} label="Falsi positivi" value={fb.counts.falso_positivo} tone="bg-red-600" />
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-sm font-bold text-slate-900">Risultati da rivedere</h3>
          {fb.problemi.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500" data-testid="feedback-problems-empty">
              Nessuna segnalazione di problemi. Usa i pulsanti su ogni bando per segnalare scaduti o falsi positivi.
            </p>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="feedback-problems">
              {fb.problemi.map((p, i) => {
                const meta = FB_META[p.feedback] || FB_META.falso_positivo;
                return (
                  <li key={i} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-800">{p.grant_title}</div>
                      <div className="truncate text-xs text-slate-400">{p.company_name}</div>
                    </div>
                    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${meta.cls}`}>
                      <meta.Icon className="h-3 w-3" />{meta.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
